import { useEffect } from 'react';
import Intercom, { shutdown } from '@intercom/messenger-js-sdk';
import useAuth from 'hooks/useAuth';
import ENV from 'environment';

/** GraphQL returns `created_at` as a string (ISO or parseable date). Intercom expects Unix seconds. */
function memberCreatedAtToUnixSeconds(createdAt?: string | null): number | undefined {
    if (!createdAt) return undefined;
    const ms = Date.parse(createdAt);
    if (!Number.isFinite(ms)) return undefined;
    const sec = Math.floor(ms / 1000);
    return sec > 0 ? sec : undefined;
}

type IntercomMemberFields = {
    _id: string;
    display_name: string;
    first_name?: string;
    last_name?: string;
    emails?: string[];
    created_at?: string | null;
};

function bootIntercomForUser(member: IntercomMemberFields): void {
    if (!ENV.INTERCOM_APP_ID) return;

    const email = member.emails?.[0];
    if (!email) return;

    const name = member.display_name?.trim() || [member.first_name, member.last_name].filter(Boolean).join(' ').trim() || undefined;

    const created_at = memberCreatedAtToUnixSeconds(member.created_at);

    Intercom({
        app_id: ENV.INTERCOM_APP_ID,
        user_id: member._id,
        name,
        email,
        ...(created_at != null ? { created_at } : {})
    });
}

function shutdownIntercom(): void {
    if (!ENV.INTERCOM_APP_ID) return;
    try {
        shutdown();
    } catch {
        // Safe if messenger was never booted
    }
}

/**
 * Keeps Intercom Messenger in sync with the logged-in member (`useAuth`).
 * Render `<IntercomSubscriber />` once inside `AuthProvider`.
 */
export function useIntercom(): void {
    const { user, isReady } = useAuth();

    useEffect(() => {
        if (!ENV.INTERCOM_APP_ID) return;
        if (!isReady) return;
        if (user?._id && user.emails?.[0]) {
            bootIntercomForUser(user);
        } else {
            shutdownIntercom();
        }
    }, [user, isReady]);
}

/** Mount once inside `<AuthProvider>` so Intercom runs after auth context is available. */
export function IntercomSubscriber(): null {
    useIntercom();
    return null;
}
